import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { invoicesAPI, clientsAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, Loader2, MoreHorizontal,
  Printer, Mail, Eye, Send, FileText, Palette
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

import LineItemsForm from '../../components/LineItemsForm';
import { generateDocumentHTML, DOCUMENT_TEMPLATES, resolveDocumentTemplate } from '../../lib/documentTemplates';
import AttachmentsSection from '../../components/AttachmentsSection';

const Invoices = () => {
  const { currentCompany } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  

  // Record payment dialog state
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_date: '', payment_method: 'bank_transfer', notes: '' });
  
  // Dialog states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    client_id: '',
    invoice_date: '',
    due_date: '',
    status: 'draft',
    items: [],
    discount: 0,
    discount_type: 'fixed',
    discount_amount: 0,
    subtotal: 0,
    tax: 0,
    total: 0,
    template_id: 'classic',
    payment_terms: 'Net 30',
    notes: '',
    amount_paid: 0,
    balance_due: 0
  });
  
  // Email form data
  const [emailData, setEmailData] = useState({
    recipient_email: '',
    recipient_name: '',
    subject: '',
    message: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, clientsRes, emailStatusRes] = await Promise.all([
        invoicesAPI.getAll(),
        clientsAPI.getAll(),
        api.get('/email/status')
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
      setEmailConfigured(emailStatusRes.data.configured);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || '-';
  };

  const getClient = (clientId) => {
    return clients.find(c => c.id === clientId);
  };

  const filteredInvoices = invoices.filter(inv => {
    const client = getClient(inv.client_id);
    return client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           inv.status?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-[#F7F6F2] text-[#5C6660]',
      sent: 'bg-[#47617A]/10 text-[#47617A]',
      paid: 'bg-[#178A4C]/10 text-[#178A4C]',
      overdue: 'bg-[#C93A3A]/10 text-[#C93A3A]',
      cancelled: 'bg-[#F7F6F2] text-[#5C6660]'
    };
    return (
      <Badge className={`${colors[status] || colors.draft} font-medium capitalize`}>
        {status}
      </Badge>
    );
  };

  // Derived invoice display: sequential number, balance, derived status
  const getInvoiceNumber = (invoice) => invoice.document_number || `INV-${invoice.id.slice(0, 8).toUpperCase()}`;
  const getInvoiceBalance = (invoice) => invoice.balance_due ?? ((invoice.total || 0) - (invoice.amount_paid || 0));
  const isPartiallyPaid = (invoice) => (invoice.amount_paid || 0) > 0 && getInvoiceBalance(invoice) > 0.001;
  const getDerivedStatus = (invoice) => {
    const balance = getInvoiceBalance(invoice);
    if ((invoice.amount_paid || 0) > 0 && balance <= 0.001) return 'paid';
    if (invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.due_date && balance > 0.001) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const parts = String(invoice.due_date).split('-').map(Number);
      const due = new Date(parts[0], (parts[1] || 1) - 1, parts[2] || 1);
      if (due < today) return 'overdue';
    }
    return invoice.status;
  };
  const calculateInvoiceTotals = (items, discount, discountType) => {
    const list = items || [];
    const subtotal = list.reduce((sum, it) => sum + (it.subtotal ?? ((it.quantity || 1) * (it.unit_price ?? it.price ?? 0))), 0);
    const tax = list.reduce((sum, it) => sum + (it.tax_amount ?? 0), 0);
    const discountAmount = discountType === 'percent' ? (subtotal * (discount || 0)) / 100 : (discount || 0);
    return { subtotal, tax, discountAmount, total: subtotal + tax - discountAmount };
  };

  // Form handlers
  const openForm = (invoice = null) => {
    if (invoice) {
      setEditingInvoice(invoice);
      setFormData({
        ...invoice,
        invoice_date: invoice.invoice_date || '',
        due_date: invoice.due_date || '',
        status: invoice.status || 'draft',
        items: (invoice.items || []).map((it) => ({
          ...it,
          product_name: it.product_name || it.product || it.name || '',
          unit_price: it.unit_price ?? it.price ?? 0,
          price: it.unit_price ?? it.price ?? 0,
          quantity: it.quantity || 1,
          tax_rate: it.tax_rate || 0,
          subtotal: it.subtotal ?? ((it.quantity || 1) * (it.unit_price ?? it.price ?? 0)),
          tax_amount: it.tax_amount ?? 0,
          line_total: it.line_total ?? ((it.subtotal ?? ((it.quantity || 1) * (it.unit_price ?? it.price ?? 0))) + (it.tax_amount ?? 0))
        })),
        discount: invoice.discount || 0,
        discount_type: invoice.discount_type || 'fixed',
        template_id: invoice.template_id || 'classic',
        payment_terms: invoice.payment_terms || 'Net 30',
        notes: invoice.notes || '',
        amount_paid: invoice.amount_paid || 0,
        balance_due: getInvoiceBalance(invoice)
      });
      
    } else {
      setEditingInvoice(null);
      setFormData({
        client_id: '',
        invoice_date: '',
        due_date: '',
        status: 'draft',
        items: [],
        discount: 0,
        discount_type: 'fixed',
        discount_amount: 0,
        subtotal: 0,
        tax: 0,
        total: 0,
        template_id: 'classic',
        payment_terms: 'Net 30',
        notes: '',
        amount_paid: 0,
        balance_due: 0
      });
      
    }
    setIsFormOpen(true);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (!formData.items || formData.items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }
    setSubmitting(true);
    try {
      const { subtotal, tax, discountAmount, total } = calculateInvoiceTotals(formData.items, formData.discount, formData.discount_type);
      const payload = {
        ...formData,
        items: formData.items.map((it) => ({
          ...it,
          product: it.product_name || it.product || '',
          price: it.unit_price ?? it.price ?? 0
        })),
        subtotal,
        tax,
        discount_amount: discountAmount,
        total,
        balance_due: total - (formData.amount_paid || 0)
      };
      if (editingInvoice) {
        await invoicesAPI.update(editingInvoice.id, payload);
        toast.success('Invoice updated successfully');
      } else {
        await invoicesAPI.create(payload);
        toast.success('Invoice created successfully');
      }
      setIsFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await invoicesAPI.delete(id);
        toast.success('Invoice deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete invoice');
      }
    }
  };

  // Record payment: creates a real payment receipt linked to the invoice
  const openPaymentDialog = (invoice) => {
    setPaymentInvoice(invoice);
    setPaymentForm({
      amount: String(getInvoiceBalance(invoice) || ''),
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: 'bank_transfer',
      notes: ''
    });
    setIsPaymentOpen(true);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!paymentInvoice) return;
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Enter a payment amount greater than zero');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/invoices/${paymentInvoice.id}/record-payment`, {
        amount,
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes
      });
      toast.success('Payment recorded');
      setIsPaymentOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  // Preview handler with template
  const openPreview = async (invoice) => {
    setSelectedInvoice(invoice);
    
    const client = getClient(invoice.client_id);
    const invoiceWithClient = {
      ...invoice,
      client_name: client?.name || 'Client',
      client_email: client?.email || '',
      client_phone: client?.phone || '',
      client_address: client?.address || '',
    };
    
    // Generate HTML using selected template
    const html = generateDocumentHTML(resolveDocumentTemplate(invoiceWithClient, currentCompany, 'invoice'), 'invoice', invoiceWithClient, currentCompany);
    setPreviewHtml(html);
    setIsPreviewOpen(true);
  };

  
    

  // Print handler with template
  const handlePrint = async (invoice) => {
    const client = getClient(invoice.client_id);
    const invoiceWithClient = {
      ...invoice,
      client_name: client?.name || 'Client',
      client_email: client?.email || '',
      client_phone: client?.phone || '',
      client_address: client?.address || '',
    };
    
    const html = generateDocumentHTML(resolveDocumentTemplate(invoiceWithClient, currentCompany, 'invoice'), 'invoice', invoiceWithClient, currentCompany);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Email handlers
  const openEmailDialog = (invoice) => {
    const client = getClient(invoice.client_id);
    setSelectedInvoice(invoice);
    setEmailData({
      recipient_email: client?.email || '',
      recipient_name: client?.name || '',
      subject: `Invoice #${getInvoiceNumber(invoice)}${currentCompany?.name ? ` from ${currentCompany.name}` : ''}`,
      message: 'Please find attached your invoice. Let us know if you have any questions.'
    });
    setIsEmailOpen(true);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailConfigured) {
      toast.error('Email service not configured. Please add Resend API key.');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/invoices/${selectedInvoice.id}/send-email`, emailData);
      toast.success(`Invoice sent to ${emailData.recipient_email}`);
      setIsEmailOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#131A17]">Invoices</h1>
          <p className="text-[#5C6660] mt-1">Manage customer invoices with print and email</p>
        </div>
        <Button
          onClick={() => openForm()}
          className="bg-teal-700 hover:bg-teal-800 rounded-[10px]"
          data-testid="add-invoice-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {/* Email status alert */}
      {!emailConfigured && (
        <div className="bg-[#FFFBEB] border border-[#F59E0B]/40 rounded-[10px] p-4 flex items-center gap-3">
          <Mail className="h-5 w-5 text-[#F59E0B]" />
          <div>
            <p className="text-sm font-medium text-[#131A17]">Email not configured</p>
            <p className="text-xs text-[#5C6660]">Add RESEND_API_KEY to enable sending invoices via email</p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A948E]" />
        <Input
          type="text"
          placeholder="Search invoices..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-10 border-[#E5E2D9]"
          data-testid="search-invoices"
        />
      </div>

      {/* Table */}
      <Card className="bg-white border-[#E5E2D9]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F7F6F2] hover:bg-[#F7F6F2]">
                  <TableHead className="text-xs font-semibold text-[#5C6660] uppercase">Invoice #</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5C6660] uppercase">Client</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5C6660] uppercase">Total</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5C6660] uppercase">Balance Due</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5C6660] uppercase">Due Date</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5C6660] uppercase">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5C6660] uppercase text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length > 0 ? (
                  filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id} className="hover:bg-[#F7F6F2]">
                      <TableCell className="font-mono text-sm text-[#5C6660]">
                        {getInvoiceNumber(invoice)}
                      </TableCell>
                      <TableCell className="text-sm font-medium text-[#131A17]">
                        {getClientName(invoice.client_id)}
                      </TableCell>
                      <TableCell className="text-sm font-semibold text-[#131A17]">
                        {formatCurrency(invoice.total)}
                      </TableCell>
                        <TableCell className="text-sm text-[#5C6660]">
                          {getInvoiceBalance(invoice) > 0.001 ? formatCurrency(getInvoiceBalance(invoice)) : '\u2014'}
                        </TableCell>
                      <TableCell className="text-sm text-[#5C6660]">
                        {invoice.due_date || '-'}
                      </TableCell>
                      <TableCell>
                  {getStatusBadge(getDerivedStatus(invoice))}
                  {isPartiallyPaid(invoice) && <p className="text-xs text-[#5C6660] mt-1">Partially paid</p>}
                </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`invoice-actions-${invoice.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openPreview(invoice)}>
                              <Eye className="h-4 w-4 mr-2" />
                              Preview
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handlePrint(invoice)}>
                              <Printer className="h-4 w-4 mr-2" />
                              Print
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => openEmailDialog(invoice)}
                              disabled={!emailConfigured}
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Send Email
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openForm(invoice)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openPaymentDialog(invoice)} disabled={getInvoiceBalance(invoice) <= 0.001}>
                Record Payment
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDelete(invoice.id)} className="text-red-600">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-32 text-center text-[#5C6660]">
                      <FileText className="h-8 w-8 mx-auto mb-2 text-[#E5E2D9]" />
                      No invoices found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Invoice Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingInvoice ? 'Edit Invoice' : 'Create Invoice'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitForm}>
          <div className="grid gap-4 py-4">
            <LineItemsForm
              items={formData.items}
              onItemsChange={(items) => setFormData({ ...formData, items })}
              clients={clients}
              onClientsChange={setClients}
              selectedClientId={formData.client_id}
              onClientChange={(id) => setFormData({ ...formData, client_id: id })}
              discount={formData.discount}
              onDiscountChange={(val) => setFormData({ ...formData, discount: val })}
              discountType={formData.discount_type}
              onDiscountTypeChange={(val) => setFormData({ ...formData, discount_type: val })}
              partyType="client"
              partyLabel="Client"
            />
            <div className="rounded-[10px] border border-[#E5E2D9] bg-[#F7F6F2] p-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label className="text-[#5C6660]">Invoice Date</Label>
                  <Input type="date" value={formData.invoice_date} onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })} data-testid="invoice-date-input" />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[#5C6660]">Due Date</Label>
                  <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} data-testid="invoice-due-date-input" />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[#5C6660]">Payment Terms</Label>
                  <Select value={formData.payment_terms} onValueChange={(val) => setFormData({ ...formData, payment_terms: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                      <SelectItem value="Net 15">Net 15</SelectItem>
                      <SelectItem value="Net 30">Net 30</SelectItem>
                      <SelectItem value="Net 45">Net 45</SelectItem>
                      <SelectItem value="Net 60">Net 60</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label className="text-[#5C6660]">Status</Label>
                  <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                    <SelectTrigger data-testid="invoice-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2 mt-4">
                <Label className="text-[#5C6660]">Notes / Terms & Conditions</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes or terms..." rows={3} data-testid="invoice-notes-input" />
              </div>
              
            </div>
          </div>
        <AttachmentsSection entityType="invoice" entityId={editingInvoice?.id} />
          <DialogFooter>
            <Button type="button" variant="outline" className="border-[#E5E2D9]" onClick={() => setIsFormOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting} data-testid="invoice-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingInvoice ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
        </DialogContent>
      </Dialog>

              {/* Record Payment Dialog */}
        <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Record Payment</DialogTitle>
              <DialogDescription>
                {paymentInvoice ? `Balance due: ${formatCurrency(getInvoiceBalance(paymentInvoice))}` : ''}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitPayment}>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label>Amount</Label>
                  <Input type="number" step="0.01" min="0" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} data-testid="payment-amount-input" />
                </div>
                <div className="grid gap-2">
                  <Label>Date</Label>
                  <Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} data-testid="payment-date-input" />
                </div>
                <div className="grid gap-2">
                  <Label>Method</Label>
                  <Select value={paymentForm.payment_method} onValueChange={(val) => setPaymentForm({ ...paymentForm, payment_method: val })}>
                    <SelectTrigger data-testid="payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Note</Label>
                  <Textarea value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} placeholder="Optional note" rows={2} data-testid="payment-notes-input" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="border-[#E5E2D9]" onClick={() => setIsPaymentOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting} data-testid="payment-submit-btn">
                  Record Payment
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

{/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5 text-teal-600" />
              Invoice Preview
            </DialogTitle>
            <DialogDescription>
              This document renders with the company's default template from Settings.
            </DialogDescription>
          </DialogHeader>
          
          
             
              
          {/* Preview */}
          <div 
            className="rounded-[10px] border border-[#E5E2D9] bg-[#F7F6F2] p-4 sm:p-6 overflow-hidden mt-4"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-[#E5E2D9]" onClick={() => setIsPreviewOpen(false)}>
              Close
            </Button>
            <Button 
              variant="outline" 
              className="border-[#E5E2D9]"
          onClick={() => selectedInvoice && handlePrint(selectedInvoice)}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button 
              className="bg-teal-700 hover:bg-teal-800"
              onClick={() => {
                setIsPreviewOpen(false);
                selectedInvoice && openEmailDialog(selectedInvoice);
              }}
              disabled={!emailConfigured}
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={isEmailOpen} onOpenChange={setIsEmailOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Send className="h-5 w-5 text-teal-600" />
              Send Invoice via Email
            </DialogTitle>
            <DialogDescription>
              Send invoice #{selectedInvoice ? getInvoiceNumber(selectedInvoice) : ''} to the client
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendEmail}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Recipient Email *</Label>
                <Input
                  type="email"
                  value={emailData.recipient_email}
                  onChange={(e) => setEmailData({...emailData, recipient_email: e.target.value})}
                  placeholder="client@example.com"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label>Recipient Name *</Label>
                <Input
                  value={emailData.recipient_name}
                  onChange={(e) => setEmailData({...emailData, recipient_name: e.target.value})}
                  placeholder="Client name"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label>Subject</Label>
                <Input
                  value={emailData.subject}
                  onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                  placeholder={currentCompany?.name ? `Invoice from ${currentCompany.name}` : 'Invoice subject'}
                />
              </div>
              <div className="grid gap-2">
                <Label>Message (optional)</Label>
                <Textarea
                  value={emailData.message}
                  onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                  placeholder="Add a personal message..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEmailOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Invoice
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
